import { haversineMeters } from './driver-attendance';

/**
 * P11 B5 PR-D – Gợi ý ghép chuyến (docs/phases/P11.md §10.4). Hàm thuần, không đọc DB.
 * Ứng viên → lọc điều kiện cứng → chèn khả thi rẻ nhất → local search (2-opt điểm giao, dời phiếu giữa chuyến)
 * → chi phí (xe nội bộ = km × đ/km + phút × đ/phút; không coi là miễn phí) → chấm điểm (trọng số chuẩn hóa) → giải thích.
 * - Khoảng cách = đường chim bay × hệ số đường (ước tính, ghi rõ); thiếu tọa độ → phiếu bị loại "chưa đủ dữ liệu", không đoán.
 * - Lộ trình mỗi chuyến: bãi xe → các điểm lấy → các điểm giao → về bãi (lấy trước giao luôn đúng).
 * - Chỉ đề xuất; người có quyền bấm áp dụng từng chuyến (backend kiểm lại điều kiện cứng).
 */
export interface GeoPoint {
  lat: number;
  lng: number;
}

export interface SuggestPlace {
  /** Khóa gộp điểm trùng (giống `delivery_trip_stops.place_key`) */
  key: string;
  label: string;
  point: GeoPoint;
}

export interface SuggestNote {
  id: number;
  code: string;
  customerName: string;
  kg: number;
  /** null = có dòng chưa có khối tích (cảnh báo, không chặn) */
  volumeM3: number | null;
  lengthM: number | null;
  /** 0 thường, 1 ưu tiên, 2 gấp */
  priority: number;
  /** Phút trong ngày (07:30 = 450) */
  windowFrom: number | null;
  windowTo: number | null;
  pickups: SuggestPlace[];
  drop: SuggestPlace | null;
  /** Điều kiện cứng đã biết trước (chưa sẵn sàng, thiếu tọa độ…) → loại, kèm lý do */
  blockers: string[];
}

export interface SuggestVehicle {
  id: number;
  plate: string;
  loadKg: number | null;
  volumeM3: number | null;
  lengthM: number | null;
  /** đ/km của xe trong kỳ (Hiệu quả xe P12); null → dùng mặc định trong cài đặt */
  costPerKm: number | null;
  depot: SuggestPlace | null;
  /** Xe không dùng được (đã có chuyến, khóa checklist…) */
  blockers: string[];
}

export interface DispatchWeights {
  cost: number;
  distance: number;
  time: number;
  utilization: number;
  stability: number;
}

export interface DispatchSettings {
  weights: DispatchWeights;
  speedKmh: number;
  loadMinutes: number;
  unloadMinutes: number;
  /** đ/phút (nhân công + chờ) */
  costPerMinute: number;
  /** đ/km khi xe chưa có số kỳ này */
  defaultCostPerKm: number;
  /** Hệ số đường so với đường chim bay */
  roadFactor: number;
  /** Giờ xe xuất bãi, phút trong ngày */
  startMinute: number;
  /** Tối đa điểm dừng 1 chuyến (an toàn vận hành) */
  maxStops: number;
}

export const DEFAULT_DISPATCH_SETTINGS: DispatchSettings = {
  weights: { cost: 35, distance: 25, time: 20, utilization: 10, stability: 10 },
  speedKmh: 25,
  loadMinutes: 20,
  unloadMinutes: 30,
  costPerMinute: 1500,
  defaultCostPerKm: 12000,
  roadFactor: 1.3,
  startMinute: 7 * 60 + 30,
  maxStops: 8,
};

export const DISPATCH_WEIGHT_LABELS: Record<keyof DispatchWeights, string> = {
  cost: 'Chi phí ròng',
  distance: 'Quãng đường, km chạy rỗng',
  time: 'Đáp ứng thời gian',
  utilization: 'Sử dụng tải / khối',
  stability: 'Ổn định vận hành',
};

export interface SuggestStop {
  kind: 'pickup' | 'drop';
  key: string;
  label: string;
  noteIds: number[];
  /** km từ điểm trước (ước tính) */
  legKm: number;
  /** Giờ tới, phút trong ngày */
  etaMinute: number;
  /** Phút chờ tới giờ khách nhận */
  waitMinutes: number;
}

export interface SuggestTrip {
  vehicleId: number;
  plate: string;
  noteIds: number[];
  stops: SuggestStop[];
  km: number;
  emptyKm: number;
  minutes: number;
  endMinute: number;
  cost: number;
  /** Tổng chi phí nếu mỗi phiếu chạy riêng */
  separateCost: number;
  saving: number;
  load: { kg: number; volumeM3: number; unknownVolumeNotes: number; maxLengthM: number | null };
  remaining: { kg: number | null; volumeM3: number | null };
  score: number;
  scoreParts: Record<keyof DispatchWeights, number>;
  reasons: string[];
  risks: string[];
}

export interface SuggestExcluded {
  noteId: number;
  code: string;
  reasons: string[];
}

export interface SuggestResult {
  trips: SuggestTrip[];
  excluded: SuggestExcluded[];
  unusedVehicles: { vehicleId: number; plate: string; reasons: string[] }[];
  totals: { cost: number; separateCost: number; saving: number; km: number };
  /** Khoảng cách là ước tính (đường chim bay × hệ số) */
  approxDistance: true;
}

const r1 = (n: number) => Math.round(n * 10) / 10;
const r3 = (n: number) => Math.round(n * 1000) / 1000;
const money = (n: number) => Math.round(n);
const fmtMoney = (n: number) => `${Math.round(n).toLocaleString('vi-VN')} đ`;
export const minuteToHHMM = (m: number) => `${String(Math.floor(m / 60) % 24).padStart(2, '0')}:${String(Math.round(m) % 60).padStart(2, '0')}`;

export function roadKm(a: GeoPoint, b: GeoPoint, roadFactor: number): number {
  return (haversineMeters(a.lat, a.lng, b.lat, b.lng) / 1000) * roadFactor;
}

/** Chuẩn hóa trọng số về tổng 1 (âm coi như 0; tổng 0 → dùng mặc định). */
export function normalizeWeights(w: DispatchWeights): DispatchWeights {
  const c = (n: number) => (Number.isFinite(n) && n > 0 ? n : 0);
  const sum = c(w.cost) + c(w.distance) + c(w.time) + c(w.utilization) + c(w.stability);
  if (sum <= 0) return normalizeWeights(DEFAULT_DISPATCH_SETTINGS.weights);
  return { cost: c(w.cost) / sum, distance: c(w.distance) / sum, time: c(w.time) / sum, utilization: c(w.utilization) / sum, stability: c(w.stability) / sum };
}

interface Route {
  vehicle: SuggestVehicle;
  notes: SuggestNote[];
  /** Thứ tự điểm giao theo khóa */
  dropOrder: string[];
}

interface Eval {
  feasible: boolean;
  why: string[];
  stops: SuggestStop[];
  km: number;
  emptyKm: number;
  minutes: number;
  endMinute: number;
  cost: number;
}

function loadOf(notes: SuggestNote[]) {
  let kg = 0;
  let vol = 0;
  let unknown = 0;
  let len: number | null = null;
  for (const n of notes) {
    kg += n.kg;
    if (n.volumeM3 === null) unknown++;
    else vol += n.volumeM3;
    if (n.lengthM !== null) len = Math.max(len ?? 0, n.lengthM);
  }
  return { kg: r3(kg), volumeM3: r3(vol), unknownVolumeNotes: unknown, maxLengthM: len };
}

/** Điều kiện cứng về tải: trả lý do vượt (rỗng = chở được). Thiếu số của xe → không kiểm ô đó. */
export function capacityIssues(v: Pick<SuggestVehicle, 'loadKg' | 'volumeM3' | 'lengthM'>, notes: SuggestNote[]): string[] {
  const l = loadOf(notes);
  const out: string[] = [];
  if (v.loadKg !== null && l.kg > v.loadKg + 1e-9) out.push(`Vượt tải: ${l.kg.toLocaleString('vi-VN')} kg > ${v.loadKg.toLocaleString('vi-VN')} kg`);
  if (v.volumeM3 !== null && l.volumeM3 > v.volumeM3 + 1e-9) out.push(`Vượt khối: ${l.volumeM3} m³ > ${v.volumeM3} m³`);
  if (v.lengthM !== null && l.maxLengthM !== null && l.maxLengthM > v.lengthM + 1e-9) out.push(`Hàng dài ${l.maxLengthM} m > xe chở được ${v.lengthM} m`);
  return out;
}

function placeOrder(start: GeoPoint | null, places: SuggestPlace[], rf: number): SuggestPlace[] {
  // Láng giềng gần nhất
  const left = [...places];
  const out: SuggestPlace[] = [];
  let at = start ?? left[0]?.point ?? null;
  while (left.length) {
    let bi = 0;
    if (at) {
      let best = Infinity;
      left.forEach((p, i) => {
        const d = roadKm(at!, p.point, rf);
        if (d < best - 1e-9 || (Math.abs(d - best) < 1e-9 && p.key < left[bi]!.key)) {
          best = d;
          bi = i;
        }
      });
    }
    const [p] = left.splice(bi, 1);
    out.push(p!);
    at = p!.point;
  }
  return out;
}

function evaluate(r: Route, s: DispatchSettings): Eval {
  const rf = s.roadFactor;
  const why: string[] = [];
  const pickMap = new Map<string, { place: SuggestPlace; noteIds: number[] }>();
  for (const n of r.notes)
    for (const p of n.pickups) {
      const e = pickMap.get(p.key) ?? { place: p, noteIds: [] };
      e.noteIds.push(n.id);
      pickMap.set(p.key, e);
    }
  const dropMap = new Map<string, { place: SuggestPlace; notes: SuggestNote[] }>();
  for (const n of r.notes) {
    const e = dropMap.get(n.drop!.key) ?? { place: n.drop!, notes: [] };
    e.notes.push(n);
    dropMap.set(n.drop!.key, e);
  }
  const depot = r.vehicle.depot?.point ?? null;
  const pickups = placeOrder(depot, [...pickMap.values()].map((x) => x.place), rf);
  const stops: SuggestStop[] = [];
  let at: GeoPoint | null = depot;
  let t = s.startMinute;
  let km = 0;
  let emptyKm = 0;
  let wait = 0;
  for (const p of pickups) {
    const leg = at ? roadKm(at, p.point, rf) : 0;
    if (stops.length === 0) emptyKm += leg;
    km += leg;
    t += (leg / s.speedKmh) * 60;
    stops.push({ kind: 'pickup', key: p.key, label: p.label, noteIds: pickMap.get(p.key)!.noteIds, legKm: r1(leg), etaMinute: Math.round(t), waitMinutes: 0 });
    t += s.loadMinutes;
    at = p.point;
  }
  for (const key of r.dropOrder) {
    const d = dropMap.get(key)!;
    const leg = at ? roadKm(at, d.place.point, rf) : 0;
    km += leg;
    t += (leg / s.speedKmh) * 60;
    const from = Math.max(...d.notes.map((n) => n.windowFrom ?? 0));
    const to = Math.min(...d.notes.map((n) => n.windowTo ?? 24 * 60));
    let w = 0;
    if (t < from) {
      w = from - t;
      t = from;
    }
    if (t > to + 1e-9) why.push(`Tới ${d.place.label} lúc ${minuteToHHMM(t)}, trễ giờ nhận (đến ${minuteToHHMM(to)})`);
    wait += w;
    stops.push({ kind: 'drop', key, label: d.place.label, noteIds: d.notes.map((n) => n.id), legKm: r1(leg), etaMinute: Math.round(t), waitMinutes: Math.round(w) });
    t += s.unloadMinutes;
    at = d.place.point;
  }
  if (depot && at) {
    const back = roadKm(at, depot, rf);
    km += back;
    emptyKm += back;
    t += (back / s.speedKmh) * 60;
  }
  if (stops.length > s.maxStops) why.push(`${stops.length} điểm dừng > tối đa ${s.maxStops}`);
  const minutes = t - s.startMinute;
  const perKm = r.vehicle.costPerKm ?? s.defaultCostPerKm;
  void wait;
  return {
    feasible: why.length === 0,
    why,
    stops,
    km: r1(km),
    emptyKm: r1(emptyKm),
    minutes: Math.round(minutes),
    endMinute: Math.round(t),
    cost: money(km * perKm + minutes * s.costPerMinute),
  };
}

/** Thứ tự điểm giao tốt nhất: chèn rẻ nhất rồi 2-opt (giữ khả thi khung giờ). */
function bestDropOrder(r: Route, s: DispatchSettings): { order: string[]; ev: Eval } {
  const keys = [...new Set(r.notes.map((n) => n.drop!.key))];
  // Chèn lần lượt (phiếu gấp / khung giờ sớm trước)
  const byKey = new Map(r.notes.map((n) => [n.drop!.key, n]));
  const seq = [...keys].sort((a, b) => {
    const na = byKey.get(a)!;
    const nb = byKey.get(b)!;
    return (na.windowTo ?? 1440) - (nb.windowTo ?? 1440) || nb.priority - na.priority || a.localeCompare(b);
  });
  let order: string[] = [];
  for (const k of seq) {
    let best: { order: string[]; ev: Eval } | null = null;
    for (let i = 0; i <= order.length; i++) {
      const cand = [...order.slice(0, i), k, ...order.slice(i)];
      const ev = evaluate({ ...r, dropOrder: cand }, s);
      if (!best || better(ev, best.ev)) best = { order: cand, ev };
    }
    order = best!.order;
  }
  let ev = evaluate({ ...r, dropOrder: order }, s);
  // 2-opt
  let improved = true;
  let guard = 0;
  while (improved && guard++ < 50) {
    improved = false;
    for (let i = 0; i < order.length - 1; i++)
      for (let j = i + 1; j < order.length; j++) {
        const cand = [...order.slice(0, i), ...order.slice(i, j + 1).reverse(), ...order.slice(j + 1)];
        const e2 = evaluate({ ...r, dropOrder: cand }, s);
        if (better(e2, ev)) {
          order = cand;
          ev = e2;
          improved = true;
        }
      }
  }
  return { order, ev };
}

/** Khả thi trước, rồi rẻ hơn. */
const better = (a: Eval, b: Eval) => (a.feasible !== b.feasible ? a.feasible : a.cost < b.cost - 0.5);

function tryRoute(vehicle: SuggestVehicle, notes: SuggestNote[], s: DispatchSettings): (Route & { ev: Eval }) | null {
  if (capacityIssues(vehicle, notes).length) return null;
  const base: Route = { vehicle, notes, dropOrder: [] };
  const { order, ev } = bestDropOrder(base, s);
  if (!ev.feasible) return null;
  return { ...base, dropOrder: order, ev };
}

/** Chi phí chạy riêng rẻ nhất của 1 phiếu trên các xe (không quan tâm xe đã dùng). */
function soloCost(n: SuggestNote, vehicles: SuggestVehicle[], s: DispatchSettings): { cost: number; why: string[] } {
  let best: number | null = null;
  const why = new Set<string>();
  for (const v of vehicles) {
    const cap = capacityIssues(v, [n]);
    if (cap.length) {
      cap.forEach((c) => why.add(c));
      continue;
    }
    const r = tryRoute(v, [n], s);
    if (!r) {
      evaluate({ vehicle: v, notes: [n], dropOrder: [n.drop!.key] }, s).why.forEach((c) => why.add(c));
      continue;
    }
    if (best === null || r.ev.cost < best) best = r.ev.cost;
  }
  return best === null ? { cost: NaN, why: [...why] } : { cost: best, why: [] };
}

function scoreTrip(ev: Eval, notes: SuggestNote[], v: SuggestVehicle, separate: number, s: DispatchSettings, w: DispatchWeights) {
  const l = loadOf(notes);
  const clamp = (n: number) => Math.max(0, Math.min(1, n));
  const cost = separate > 0 ? clamp((separate - ev.cost) / separate / 0.5) : 0; // tiết kiệm 50 % trở lên = 1
  const distance = ev.km > 0 ? clamp(1 - ev.emptyKm / ev.km) : 1;
  const slack = ev.stops
    .filter((x) => x.kind === 'drop')
    .map((x) => {
      const to = Math.min(...notes.filter((n) => x.noteIds.includes(n.id)).map((n) => n.windowTo ?? 24 * 60));
      return clamp((to - x.etaMinute) / 120);
    });
  const time = slack.length ? slack.reduce((a, b) => a + b, 0) / slack.length : 1;
  const uk = v.loadKg ? l.kg / v.loadKg : 0;
  const uv = v.volumeM3 && !l.unknownVolumeNotes ? l.volumeM3 / v.volumeM3 : 0;
  const utilization = clamp(Math.max(uk, uv));
  const stability = clamp(1 - 0.08 * Math.max(0, ev.stops.length - 2) - 0.15 * l.unknownVolumeNotes);
  const score = Math.round(100 * (w.cost * cost + w.distance * distance + w.time * time + w.utilization * utilization + w.stability * stability));
  const round2 = (n: number) => Math.round(n * 100) / 100;
  return {
    score,
    parts: { cost: round2(cost), distance: round2(distance), time: round2(time), utilization: round2(utilization), stability: round2(stability) },
  };
}

/**
 * Gợi ý chuyến cho các phiếu chưa vào chuyến. Xe có `blockers` không dùng; phiếu có `blockers` bị loại kèm lý do.
 */
export function suggestTrips(notesIn: SuggestNote[], vehiclesIn: SuggestVehicle[], settings: DispatchSettings = DEFAULT_DISPATCH_SETTINGS): SuggestResult {
  const s = settings;
  const w = normalizeWeights(s.weights);
  const excluded: SuggestExcluded[] = [];
  const vehicles = vehiclesIn.filter((v) => !v.blockers.length);
  const unusedVehicles = vehiclesIn.filter((v) => v.blockers.length).map((v) => ({ vehicleId: v.id, plate: v.plate, reasons: v.blockers }));
  const candidates: SuggestNote[] = [];
  for (const n of notesIn) {
    const why = [...n.blockers];
    if (!n.drop) why.push('Chưa có tọa độ điểm giao: chưa đủ dữ liệu để tính tuyến');
    if (!n.pickups.length) why.push('Chưa có điểm lấy hàng (phiếu chưa chia kho)');
    if (why.length) excluded.push({ noteId: n.id, code: n.code, reasons: why });
    else candidates.push(n);
  }
  if (!vehicles.length) {
    for (const n of candidates) excluded.push({ noteId: n.id, code: n.code, reasons: ['Không còn xe công ty trống trong ngày: lập chuyến xe thuê'] });
    return { trips: [], excluded, unusedVehicles, totals: { cost: 0, separateCost: 0, saving: 0, km: 0 }, approxDistance: true };
  }
  const solo = new Map<number, number>();
  const usable: SuggestNote[] = [];
  for (const n of candidates) {
    const sc = soloCost(n, vehicles, s);
    if (Number.isNaN(sc.cost)) excluded.push({ noteId: n.id, code: n.code, reasons: sc.why.length ? sc.why : ['Không xe nào chở được'] });
    else {
      solo.set(n.id, sc.cost);
      usable.push(n);
    }
  }
  // Phiếu gấp, giờ nhận sớm, nặng trước
  usable.sort((a, b) => b.priority - a.priority || (a.windowTo ?? 1440) - (b.windowTo ?? 1440) || b.kg - a.kg || a.id - b.id);

  type Built = Route & { ev: Eval };
  const routes: Built[] = [];
  const free = () => vehicles.filter((v) => !routes.some((r) => r.vehicle.id === v.id));
  for (const n of usable) {
    let best: { idx: number; built: Built; delta: number } | null = null;
    routes.forEach((r, idx) => {
      const b = tryRoute(r.vehicle, [...r.notes, n], s);
      if (!b) return;
      const delta = b.ev.cost - r.ev.cost;
      if (!best || delta < best.delta - 0.5) best = { idx, built: b, delta };
    });
    for (const v of free()) {
      const b = tryRoute(v, [n], s);
      if (!b) continue;
      if (!best || b.ev.cost < best.delta - 0.5) best = { idx: -1, built: b, delta: b.ev.cost };
    }
    if (!best) {
      excluded.push({ noteId: n.id, code: n.code, reasons: ['Các xe trống không còn chỗ / không kịp giờ nhận: lập chuyến xe thuê hoặc giao ngày khác'] });
      continue;
    }
    const pick = best as { idx: number; built: Built; delta: number };
    if (pick.idx < 0) routes.push(pick.built);
    else routes[pick.idx] = pick.built;
  }

  // Local search: dời 1 phiếu sang chuyến khác nếu tổng chi phí giảm
  let moved = true;
  let guard = 0;
  while (moved && guard++ < 20) {
    moved = false;
    for (let i = 0; i < routes.length && !moved; i++)
      for (const n of routes[i]!.notes) {
        const rest = routes[i]!.notes.filter((x) => x.id !== n.id);
        const from = rest.length ? tryRoute(routes[i]!.vehicle, rest, s) : null;
        if (rest.length && !from) continue;
        const fromCost = from?.ev.cost ?? 0;
        for (let j = 0; j < routes.length; j++) {
          if (j === i) continue;
          const to = tryRoute(routes[j]!.vehicle, [...routes[j]!.notes, n], s);
          if (!to) continue;
          if (fromCost + to.ev.cost < routes[i]!.ev.cost + routes[j]!.ev.cost - 0.5) {
            routes[j] = to;
            if (from) routes[i] = from;
            else routes.splice(i, 1);
            moved = true;
            break;
          }
        }
        if (moved) break;
      }
  }

  const trips: SuggestTrip[] = routes.map((r) => {
    const separate = r.notes.reduce((a, n) => a + (solo.get(n.id) ?? 0), 0);
    const l = loadOf(r.notes);
    const sc = scoreTrip(r.ev, r.notes, r.vehicle, separate, s, w);
    const reasons: string[] = [];
    const risks: string[] = [];
    if (r.notes.length > 1) {
      reasons.push(`Ghép ${r.notes.length} phiếu: chạy riêng ${fmtMoney(separate)}, ghép ${fmtMoney(r.ev.cost)} → tiết kiệm ${fmtMoney(separate - r.ev.cost)}`);
      const pk = r.ev.stops.filter((x) => x.kind === 'pickup');
      if (pk.length === 1) reasons.push(`Cùng lấy tại ${pk[0]!.label}`);
      const legs = r.ev.stops.filter((x) => x.kind === 'drop').slice(1);
      if (legs.length && legs.every((x) => x.legKm <= 10)) reasons.push('Các điểm giao gần nhau (≤ 10 km giữa 2 điểm)');
    } else reasons.push('Chạy riêng: không phiếu nào ghép chung rẻ hơn');
    if (r.notes.some((n) => n.priority > 0)) reasons.push('Có phiếu ưu tiên / gấp: xếp trước');
    reasons.push(`Còn trống ${r.vehicle.loadKg !== null ? `${(r.vehicle.loadKg - l.kg).toLocaleString('vi-VN')} kg` : '? kg'}${r.vehicle.volumeM3 !== null ? `, ${r1(r.vehicle.volumeM3 - l.volumeM3)} m³` : ''}`);
    if (l.unknownVolumeNotes) risks.push(`${l.unknownVolumeNotes} phiếu thiếu khối tích: kiểm thùng xe bằng mắt`);
    if (r.vehicle.loadKg === null) risks.push('Xe chưa có tải trọng: chưa kiểm vượt tải');
    if (r.vehicle.volumeM3 === null) risks.push('Xe chưa khai thùng: chưa kiểm khối');
    for (const x of r.ev.stops.filter((y) => y.kind === 'drop')) {
      const to = Math.min(...r.notes.filter((n) => x.noteIds.includes(n.id)).map((n) => n.windowTo ?? 24 * 60));
      if (to < 24 * 60 && to - x.etaMinute < 30) risks.push(`${x.label}: tới ${minuteToHHMM(x.etaMinute)}, sát giờ nhận ${minuteToHHMM(to)}`);
    }
    if (r.vehicle.costPerKm === null) risks.push(`Xe chưa có đ/km kỳ này: dùng mặc định ${fmtMoney(s.defaultCostPerKm)}/km`);
    risks.push('Km, giờ tới là ước tính (đường chim bay × hệ số đường)');
    return {
      vehicleId: r.vehicle.id,
      plate: r.vehicle.plate,
      noteIds: r.notes.map((n) => n.id),
      stops: r.ev.stops,
      km: r.ev.km,
      emptyKm: r.ev.emptyKm,
      minutes: r.ev.minutes,
      endMinute: r.ev.endMinute,
      cost: r.ev.cost,
      separateCost: money(separate),
      saving: money(separate - r.ev.cost),
      load: l,
      remaining: {
        kg: r.vehicle.loadKg === null ? null : r3(r.vehicle.loadKg - l.kg),
        volumeM3: r.vehicle.volumeM3 === null ? null : r3(r.vehicle.volumeM3 - l.volumeM3),
      },
      score: sc.score,
      scoreParts: sc.parts,
      reasons,
      risks,
    };
  });
  trips.sort((a, b) => b.score - a.score || a.vehicleId - b.vehicleId);
  for (const v of vehicles) if (!routes.some((r) => r.vehicle.id === v.id)) unusedVehicles.push({ vehicleId: v.id, plate: v.plate, reasons: ['Không cần dùng'] });
  return {
    trips,
    excluded,
    unusedVehicles,
    totals: {
      cost: trips.reduce((a, t) => a + t.cost, 0),
      separateCost: trips.reduce((a, t) => a + t.separateCost, 0),
      saving: trips.reduce((a, t) => a + t.saving, 0),
      km: r1(trips.reduce((a, t) => a + t.km, 0)),
    },
    approxDistance: true,
  };
}
